import clsx from "clsx"
import React, { ReactElement, useCallback, useReducer, useState } from "react"
import Button from "../../fundamentals/button"
import Modal, { ModalProps } from "../../molecules/modal"
import LayeredModal, { ILayeredModalContext } from "./layered-modal"

enum SteppedActions {
  ENABLENEXTPAGE,
  DISABLENEXTPAGE,
  GOTONEXTPAGE,
  GOTOPREVIOUSPAGE,
  SETPAGE,
  SUBMIT,
  RESET,
}

type ISteppedContext = {
  currentStep: number
  nextStepEnabled: boolean
  enableNextPage: () => void
  disableNextPage: () => void
  goToNextPage: () => void
  goToPreviousPage: () => void
  submit: () => void
  reset: () => void
  setPage: (page: number) => void
}

const defaultContext: ISteppedContext = {
  currentStep: 0,
  nextStepEnabled: true,
  enableNextPage: () => {},
  disableNextPage: () => {},
  goToNextPage: () => {},
  goToPreviousPage: () => {},
  submit: () => {},
  reset: () => {},
  setPage: (page) => {},
}

export const SteppedContext = React.createContext(defaultContext)

const reducer = (state, action) => {
  switch (action.type) {
    case SteppedActions.ENABLENEXTPAGE: {
      return { ...state, nextStepEnabled: true }
    }
    case SteppedActions.DISABLENEXTPAGE: {
      return { ...state, nextStepEnabled: false }
    }
    case SteppedActions.GOTONEXTPAGE: {
      return { ...state, currentStep: state.currentStep + 1 }
    }
    case SteppedActions.GOTOPREVIOUSPAGE: {
      return { ...state, currentStep: Math.max(0, state.currentStep - 1) }
    }
    case SteppedActions.SETPAGE: {
      return {
        ...state,
        currentStep: action.payload > 0 ? action.payload : state.currentStep,
      }
    }
    case SteppedActions.SUBMIT: {
      return { ...state }
    }
    case SteppedActions.RESET: {
      return { ...state, currentStep: 0, nextStepEnabled: true }
    }
  }
}

type SteppedProps = {
  context: ISteppedContext
  title: string
  onSubmitStep?: (step: number) => Promise<void>
  onSubmit: () => void
  lastScreenIsSummary?: boolean
  steps: ReactElement<any, string>[]
  layeredContext?: ILayeredModalContext
  submitButtonLabel?: string
  isLoading?: boolean
  closeOnOverlayClick?: boolean
} & ModalProps

export const SteppedProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, defaultContext)
  const enableNextPage = useCallback(() => {
    dispatch({ type: SteppedActions.ENABLENEXTPAGE })
  }, [])
  const disableNextPage = useCallback(() => {
    dispatch({ type: SteppedActions.DISABLENEXTPAGE })
  }, [])

  return (
    <SteppedContext.Provider
      value={{
        ...state,
        enableNextPage,
        disableNextPage,
        goToNextPage: () => {
          dispatch({ type: SteppedActions.GOTONEXTPAGE })
        },
        goToPreviousPage: () => {
          dispatch({ type: SteppedActions.GOTOPREVIOUSPAGE })
        },
        submit: () => {
          dispatch({ type: SteppedActions.SUBMIT })
        },
        setPage: (page: number) => {
          dispatch({ type: SteppedActions.SETPAGE, payload: page })
        },
        reset: () => {
          dispatch({ type: SteppedActions.RESET })
        },
      }}
    >
      {children}
    </SteppedContext.Provider>
  )
}

const SteppedModal: React.FC<SteppedProps> = ({
  context,
  steps,
  layeredContext,
  title,
  onSubmitStep = (_step) => Promise.resolve(),
  onSubmit,
  lastScreenIsSummary = false,
  handleClose,
  isLargeModal = true,
  submitButtonLabel = "Submit",
  isLoading = false,
  closeOnOverlayClick = false,
}) => {
  const resetAndClose = () => {
    context.reset()
    handleClose()
  }
  const [isLoadingStep, setIsLoadingStep] = useState(false)

  const resetAndSubmit = () => {
    onSubmit()
  }

  const onNextStepClicked = () => {
    setIsLoadingStep(true)
    onSubmitStep(context.currentStep)
      .then(() => {
        context.currentStep === steps.length - 1
          ? resetAndSubmit()
          : context.goToNextPage()
        setIsLoadingStep(false)
      })
      .catch(() => {
        setIsLoadingStep(false)
      })
  }

  return (
    <ModalElement
      layeredContext={layeredContext}
      isLargeModal={isLargeModal}
      handleClose={resetAndClose}
      closeOnOverlayClick={closeOnOverlayClick}
    >
      <Modal.Body
        className={clsx(
          "transition-transform flex flex-col justify-between duration-100 max-h-full"
        )}
      >
        <Modal.Header handleClose={resetAndClose}>
          <div className="flex flex-col">
            <h2 className="inter-xlarge-semibold">{title}</h2>
            {!lastScreenIsSummary ||
              (lastScreenIsSummary &&
                context.currentStep !== steps.length - 1 && (
                  <div className="flex items-center">
                    <span className="text-grey-50 inter-small-regular w-[70px] mr-4">{`Step ${
                      context.currentStep + 1
                    } of ${steps.length}`}</span>
                    {steps.map((_, i) => (
                      <span
                        key={i}
                        className={clsx(
                          "w-2 h-2 rounded-full mr-3",
                          {
                            "bg-grey-20": i > context.currentStep,
                            "bg-violet-60": context.currentStep >= i,
                          },
                          {
                            "outline-4 outline outline-violet-20":
                              context.currentStep === i,
                          }
                        )}
                      />
                    ))}
                  </div>
                ))}
          </div>
        </Modal.Header>
        <Modal.Content>{steps[context.currentStep]}</Modal.Content>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end w-full gap-x-xsmall">
          <Button
            variant="ghost"
            size="small"
            disabled={context.currentStep === 0 || isLoading || isLoadingStep}
            onClick={() => context.goToPreviousPage()}
            className="w-[112px]"
          >
            Back
          </Button>
          <Button
            variant="primary"
            size="small"
            disabled={!context.nextStepEnabled || isLoading || isLoadingStep}
            loading={isLoading || isLoadingStep}
            onClick={onNextStepClicked}
            className="w-[112px]"
          >
            {context.currentStep === steps.length - 1
              ? submitButtonLabel
              : "Next"}
          </Button>
        </div>
      </Modal.Footer>
    </ModalElement>
  )
}

const ModalElement = ({
  layeredContext,
  handleClose,
  isLargeModal = true,
  children,
  closeOnOverlayClick = false,
}) =>
  layeredContext ? (
    <LayeredModal
      context={layeredContext}
      handleClose={handleClose}
      isLargeModal={isLargeModal}
      closeOnOverlayClick={closeOnOverlayClick}
    >
      {children}
    </LayeredModal>
  ) : (
    <Modal handleClose={handleClose} isLargeModal={isLargeModal}>
      {children}
    </Modal>
  )

export default SteppedModal
